import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogContent, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { SolProductPromotionRequestDialog } from "../../../dialogs/sol/sol-product-promotion-request/sol-product-promotion-request.component";
import { debounceTime, distinctUntilChanged, startWith, Subject, takeUntil } from "rxjs";
import { CommonDataService } from "../../../../services/common-data.service";
import {
  EstimateSettings, Material, PpaStatus, ProductPromotionModel, ProductPromotionCollection,
  ProductPromotionDropDownDataRequest, ProductPromotionDropDownDataResponse,
  ProductPromotionRequest, ProductPromotionStatusDetails, ProductPromotionStatusRequest, ProductPromotionStatusResponse,
  ProductPromotionMaterialType
} from "../../../../models/dtos";
import { IProductPromotionAppliedDiscount } from "../../../../models/common/IProductPromotionAppliedDiscount";
import { CONSTANTS } from "../../../../Constants/Constants";
import { ProductPromotionService } from "../../../../services/api/product-promotion.service";
import { ProductPromotionRequestDialogData } from "../../../../models/common/ProductPromotionRequestDialogData";
import { TranslatePipe } from "@ngx-translate/core";
import { SolCalculationsService } from "../../../../services/operations/sol-calculations.service";
import { TrackChangesDirective } from "../../../../directives/track-changes.directive";
import { DecimalPrecisionPipe } from "../../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
import { NumericMaskDirective } from "../../../../shared/numeric/numeric-mask.directive";

@Component({
  selector: 'app-sol-product-promotion-dialog',
  templateUrl: './sol-product-promotion.component.html',
  styleUrl: './sol-product-promotion.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    TranslatePipe,
    TrackChangesDirective,
    DecimalPrecisionPipe,
    NumericMaskDirective
  ]
})
export class SolProductPromotionDialog implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Output() closeDialogEvent = new EventEmitter<void>();
  @Input() isEstimateSapSubmitted: boolean = false;
  isAppReadOnly = false;
  private isAllowableDiscountDollarDisabled = false;

  get isReadOnly(): boolean {
    return this.isEstimateSapSubmitted || this.isAppReadOnly;
  }

  productPromotionForm: FormGroup<{
    authorizationCode: FormControl<string>;
    allowableDiscountDollars: FormControl<number>;
    allowableDiscountPercentage: FormControl<number>;
    assignMaterialDiscountDropdown: FormControl<string>;
    assignMaterialDiscount: FormControl<number>;
  }> = new FormGroup({
    authorizationCode: new FormControl('', [Validators.maxLength(13), Validators.pattern(/^[A-Za-z0-9]*$/)]),
    allowableDiscountDollars: new FormControl(0, [Validators.maxLength(12)]),
    allowableDiscountPercentage: new FormControl(0),
    assignMaterialDiscountDropdown: new FormControl(''),
    assignMaterialDiscount: new FormControl(0)
  });
  business: string = '';

  assignedMaterialsList: ProductPromotionMaterialType[] = [];
  originalAssignedMaterialsList: ProductPromotionMaterialType[] = [];

  appliedDiscountsList: IProductPromotionAppliedDiscount[] = [];
  showStatusBtn: boolean = false;
  requestedPromotionDetails: ProductPromotionStatusResponse = null;

  readonly numericFieldsConfig = {
    allowableDiscountDollarsConfig: {
      kind: 'currency',
      scale: CONSTANTS.TWO,
      min: CONSTANTS.ZERO,
      max: CONSTANTS.PRODUCT_PROMOTION_DISCOUNT_DOLLARS_MAX,
      useGrouping: true,
      showStepper: false,
      step: CONSTANTS.ONE,
      revertToLastValidValue: true
    },
    allowableDiscountPercentageConfig: {
      kind: 'percentage',
      scale: CONSTANTS.ZERO,
      min: CONSTANTS.ZERO,
      max: CONSTANTS.HUNDRED,
      step: CONSTANTS.ONE,
      useGrouping: false,
      revertToLastValidValue: true,
    },
    assignMaterialDiscountConfig: {
      kind: 'percentage',
      scale: CONSTANTS.TWO,
      min: CONSTANTS.ZERO,
      max: CONSTANTS.NINETY_NINE_POINT_NINE_NINE,
      step: CONSTANTS.ZERO_POINT_ONE,
      useGrouping: false,
      revertToLastValidValue: true,
    }
  } satisfies Record<string, NumericFieldConfig>;

  constructor(
    private readonly matDialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly productPromotionService: ProductPromotionService,
    private readonly solCalculationsService: SolCalculationsService
  ) { }

  trackByMaterialId(index: number, material: ProductPromotionMaterialType): string {
    return material.materialId;
  }

  ngOnInit(): void {
    this.business = this.commonDataService.getBusiness() || CONSTANTS.EMPTY_STRING;
    this.setupReadOnlyMode();
    this.loadProductPromotionDropDownData();
    this.subscribeToAssignedDiscountPercent();
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReadOnly => {
        this.isAppReadOnly = isReadOnly;
        this.syncFormReadOnlyState();
      });
  }

  private syncFormReadOnlyState(): void {
    if (this.isReadOnly) {
      this.productPromotionForm.disable({ emitEvent: false });
      return;
    }

    this.productPromotionForm.enable({ emitEvent: false });
    if (this.isAllowableDiscountDollarDisabled) {
      this.productPromotionForm.controls.allowableDiscountDollars.disable({ emitEvent: false });
    }
    if (this.requestedPromotionDetails?.productPromotionStatusDetails?.ppaStatus === PpaStatus.Approved) {
      this.productPromotionForm.controls.authorizationCode.disable({ emitEvent: false });
      this.productPromotionForm.controls.allowableDiscountDollars.disable({ emitEvent: false });
      this.productPromotionForm.controls.allowableDiscountPercentage.disable({ emitEvent: false });
    } else if (this.requestedPromotionDetails?.productPromotionStatusDetails?.ppaStatus === PpaStatus.NotApproved) {
      this.productPromotionForm.controls.allowableDiscountDollars.disable({ emitEvent: false });
    }
  }

  private loadProductPromotionDropDownData(): void {
    const requestBody: ProductPromotionDropDownDataRequest = {
      estimateId: this.commonDataService.getEstimateId() || CONSTANTS.EMPTY_STRING,
      cultureId: CONSTANTS.ONE,
      business: this.business
    };

    this.productPromotionService.getProductPromotionDropDownData(requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProductPromotionDropDownDataResponse) => {
          const productPromotionMaterialTypes = response?.productPromotionMaterialTypes || [];
          this.originalAssignedMaterialsList = [...productPromotionMaterialTypes];
          this.assignedMaterialsList = [...productPromotionMaterialTypes];
          this.isAllowableDiscountDollarDisabled = !!response?.isAllowableDiscountDollarDisabled;
          const allowableDiscountDollarsControl = this.productPromotionForm.controls.allowableDiscountDollars;
          if (this.isAllowableDiscountDollarDisabled) {
            allowableDiscountDollarsControl.disable();
          } else {
            allowableDiscountDollarsControl.enable();
          }
          this.prefillProductPromotionData();
          this.syncFormReadOnlyState();
        },
        error: (error) => {
          console.error('Error fetching product promotion dropdown data:', error);
        }
      });
  }
  assignedMaterialDiscountDropDownChanged(): void {
    this.checkEstimateStatus();
    this.productPromotionForm.controls.assignMaterialDiscount.setValue(0);
    this.setDefaults();
  }

  prefillProductPromotionData() {
    const estimateSettings: EstimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    this.prefillAffectedMaterial();
    if (estimateSettings && estimateSettings.productPromotionList) {
      estimateSettings.productPromotionList.forEach((promotion: ProductPromotionModel) => {
        this.prefillAppliedDiscountsList(promotion); // To populate the applied discounts list based on the pre-filled data
      });
      this.setDefaults();
    }
  }

  prefillAffectedMaterial() {
    if (this.business === CONSTANTS.BUSINESS_FIRE || this.business === CONSTANTS.BUSINESS_SECURITY) {
      return;
    }
    this.productPromotionForm.controls.assignMaterialDiscountDropdown.setValue(this.getAssignedMaterialLabel('S'));
    this.checkEstimateStatus();
  }

  prefillAuthorizationCodeDiscount() {
    if (this.requestedPromotionDetails && this.requestedPromotionDetails.productPromotionStatusDetails) {
      const statusDetails = this.requestedPromotionDetails.productPromotionStatusDetails
      // Excluding value re-set for AuthCode control to retain existing value
      this.productPromotionForm.controls.allowableDiscountDollars.setValue(0);
      this.productPromotionForm.controls.allowableDiscountPercentage.setValue(0);
      if (statusDetails.ppaStatus === PpaStatus.Approved) {
        this.productPromotionForm.controls.authorizationCode.setValue(statusDetails.authorizationCode);
        this.productPromotionForm.controls.allowableDiscountDollars.setValue(statusDetails.allowableDiscountDollars);
        this.productPromotionForm.controls.allowableDiscountPercentage.setValue(statusDetails.allowableDiscountPercent);
        this.productPromotionForm.controls.authorizationCode.disable();
        this.productPromotionForm.controls.allowableDiscountDollars.disable();
        this.productPromotionForm.controls.allowableDiscountPercentage.disable();
        this.showStatusBtn = false;
      } else if (statusDetails.ppaStatus === PpaStatus.NotApproved) {
        this.productPromotionForm.controls.allowableDiscountDollars.disable();
        this.productPromotionForm.controls.allowableDiscountPercentage.enable();
        this.productPromotionForm.controls.authorizationCode.enable();
        this.showStatusBtn = false;
      } else if (statusDetails.ppaStatus === PpaStatus.Pending) {
        this.productPromotionForm.controls.allowableDiscountDollars.enable();
        this.productPromotionForm.controls.allowableDiscountPercentage.enable();
        this.productPromotionForm.controls.authorizationCode.enable();
        this.showStatusBtn = true;
      }
      this.syncFormReadOnlyState();
    }
  }

  checkEstimateStatus() {
    const estimateId = this.commonDataService.getEstimateId();
    const request: ProductPromotionStatusRequest = {
      estimateId: estimateId || '',
      affectedMaterial: this.getAffectedMaterialValue()
    };
    this.productPromotionService.getProductPromotionStatus(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProductPromotionStatusResponse) => {
          this.requestedPromotionDetails = response;
          if (response && response.productPromotionStatusDetails) {
            if (response.productPromotionStatusDetails.ppaStatus === PpaStatus.Pending) {
              this.showStatusBtn = true;
            } else {
              this.showStatusBtn = false;
            }
            this.prefillAuthorizationCodeDiscount();
          }
        },
        error: (error) => {
          console.error('Error fetching product promotion status:', error);
          this.showStatusBtn = false; // Reset status button on error
        }
      });
  }

  removeAffectedMaterialFromDropdown(affectedMaterial: string) {
    return this.assignedMaterialsList.filter(mat => mat.materialType !== affectedMaterial);
  }

  prefillAppliedDiscountsList(promotion: ProductPromotionModel) {
    const appliedDiscountObject: IProductPromotionAppliedDiscount = {
      authCode: promotion.authorizationCode,
      materialDiscountSelected: promotion.affectedMaterialText,
      discount: promotion.discount,
      allowableDiscountDollars: promotion.allowableDiscountDollars,
      allowableDiscountPercentage: promotion.allowableDiscountPercentage,
      isRequestPending: promotion.isRequestPending
    };
    this.appliedDiscountsList.push(appliedDiscountObject);
    this.assignedMaterialsList = this.removeAffectedMaterialFromDropdown(promotion.affectedMaterialText);
  }

  onApplyBtnClick(): void {
    if (this.isReadOnly) {
      return;
    }
    const { assignMaterialDiscount, allowableDiscountPercentage } = this.productPromotionForm.controls;
    const max = Math.min(allowableDiscountPercentage.value ?? 0, CONSTANTS.NINETY_NINE_POINT_NINE_NINE);
    if ((assignMaterialDiscount.value ?? 0) > max) {
      assignMaterialDiscount.setValue(max, { emitEvent: false });
    }
    const appliedDiscountObject = {
      authCode: this.getFormControlValue('authorizationCode'),
      materialDiscountSelected: this.getAssignedMaterialValue(),
      discount: this.getFormControlValueAsNumber('assignMaterialDiscount'),
      allowableDiscountDollars: this.getFormControlValueAsNumber('allowableDiscountDollars'),
      allowableDiscountPercentage: this.getFormControlValueAsNumber('allowableDiscountPercentage'),
      isRequestPending: false
    };

    this.appliedDiscountsList.push(appliedDiscountObject);
    this.saveProductPromotions(appliedDiscountObject);
    const selectedMaterialValue = this.getAssignedMaterialValue();
    const selectedMaterialLabel = this.getAssignedMaterialLabel(selectedMaterialValue);
    this.assignedMaterialsList = this.removeAffectedMaterialFromDropdown(selectedMaterialLabel);
    if (CONSTANTS.SIEMENS_MATERIAL_TYPES.has(appliedDiscountObject.materialDiscountSelected) && appliedDiscountObject.discount > 0) {
      this.triggerProdProCostCalc(appliedDiscountObject.discount);
    }
    this.assignedMaterialsList = this.removeAffectedMaterialFromDropdown(appliedDiscountObject.materialDiscountSelected);
    // re-setting the assign material discount dropdown to its default empty state
    if (this.showMaterialDropdown()) {
      this.productPromotionForm.controls.assignMaterialDiscountDropdown.setValue(CONSTANTS.EMPTY_STRING);
    }
  }

  async triggerProdProCostCalc(discount: number, isRemove: boolean = false): Promise<void> {
    if (this.isReadOnly) {
      return;
    }

    await this.solCalculationsService.applyMaterialCostChangeAndRecalculate(
      (materialItem: Material) => this.modifyMaterialCost(materialItem, discount, isRemove)
    );
  }

  /**
   * Material cost is persisted net of the promotion (desktop convention), and `isPPA` marks the
   * materials that carry a discounted cost so the actual cost can be derived during calculation.
   */
  modifyMaterialCost(materialItem: Material, discount: number, isRemove: boolean): void {
    if (this.isReadOnly) {
      return;
    }

    if (!CONSTANTS.PRODUCT_PROMOTION_MATERIAL_TYPES.has(materialItem.materialType)) {
      return;
    }
    if (!materialItem.isRPN || materialItem.ppType === CONSTANTS.EMPTY_PP_TYPE) {
      return;
    }
    const effectiveDiscount = this.getEffectiveDiscount(discount);
    if (isRemove) {
      if (!materialItem.isPPA) {
        return;
      }
      materialItem.cost = materialItem.cost / (1 - (effectiveDiscount / CONSTANTS.HUNDRED));
      materialItem.isPPA = false;
      return;
    }
    if (materialItem.isPPA) {
      return;
    }
    materialItem.cost = materialItem.cost - (materialItem.cost * (effectiveDiscount / CONSTANTS.HUNDRED));
    materialItem.isPPA = true;
  }

  /**
   * Caps a 100% discount at 99.99% so the discounted cost never becomes zero — a zero cost
   * carries no trace of the original and could not be restored on remove.
   * Both apply and remove must use this same value for the restore to be exact.
   */
  private getEffectiveDiscount(discount: number): number {
    return discount >= CONSTANTS.HUNDRED ? CONSTANTS.NINETY_NINE_POINT_NINE_NINE : discount;
  }

  getFormControlValue(controlName: string): string {
    return this.productPromotionForm.get(controlName)?.value || '';
  }

  getAssignedMaterialValue(): string {
    if (!this.assignedMaterialsList || !this.assignedMaterialsList.length) {
      return CONSTANTS.EMPTY_STRING;
    }
    const material = this.productPromotionForm.controls.assignMaterialDiscountDropdown.value;
    if (!material) {
      return CONSTANTS.EMPTY_STRING;
    }
    return material.length === 1 ? this.getAssignedMaterialLabel(material) : material;
  }

  getFormControlValueAsNumber(controlName: string): number {
    return Number(this.productPromotionForm.get(controlName)?.value) || 0;
  }

  private getAssignedMaterialLabel(value: string): string {
    const material = this.assignedMaterialsList.find(mat => mat.materialId === value);
    return material ? material.materialType : '';
  }

  onRequestBtnClick(isRequested: boolean = false): void {
    if (this.isReadOnly) {
      return;
    }

    const data: ProductPromotionRequestDialogData = {
      affectedMaterial: this.getAffectedMaterialValue(),//it gives affected material text
      affectedMaterialText: this.getAssignedMaterialLabel(this.getAffectedMaterialValue()),
      business: this.business,
      isRequested: isRequested,
      status: this.requestedPromotionDetails?.productPromotionStatusDetails?.ppaStatus || null,
      allowedDiscoutnDollars: this.productPromotionForm.controls.allowableDiscountDollars.value || 0,
      allowedDiscountPercentage: this.productPromotionForm.controls.allowableDiscountPercentage.value || 0,
      authCode: this.productPromotionForm.controls.authorizationCode.value || '',
      statusDetails: this.requestedPromotionDetails?.productPromotionStatusDetails
    }
    const dialogRef = this.matDialog.open(SolProductPromotionRequestDialog, {
      width: '900px',
      disableClose: true,
      data: data
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result && result.isRequested) {
          this.showStatusBtn = true;
          this.updateStatusDetailsFromSubmittedRequest(result.submittedRequest);
        }
      });
  }

  private updateStatusDetailsFromSubmittedRequest(request: ProductPromotionRequest | null): void {
    if (!request) {
      return;
    }

    const statusDetails: ProductPromotionStatusDetails = {
      ppaStatus: PpaStatus.Pending,
      authorizationCode: '',
      description: request.description,
      preferenceExpected: request.preferenceExpected,
      contactNumber: request.contactNumber,
      requiredGM: request.requiredGM,
      proposalDate: request.proposalDate,
      requiredDiscountPercent: request.requiredDiscountPercent,
      jobStartDate: request.jobStartDate,
      customer: request.customer,
      requestor: request.requestor,
      endUser: request.endUser,
      cpsMaterialValue: request.cpsMaterialValue,
      estimatedSellPrice: request.estimatedSellPrice,
      gmPercentage: request.gmPercentage,
      overrideRequirements: request.overrideRequirements,
      allowableDiscountDollars: this.productPromotionForm.controls.allowableDiscountDollars.value || 0,
      allowableDiscountPercent: this.productPromotionForm.controls.allowableDiscountPercentage.value || 0
    };

    this.requestedPromotionDetails = { productPromotionStatusDetails: statusDetails };
  }

  onRemoveBtnClick(discountObj: IProductPromotionAppliedDiscount): void {
    if (this.isReadOnly) {
      return;
    }

    this.appliedDiscountsList = this.appliedDiscountsList.filter(discount => discount.materialDiscountSelected !== discountObj.materialDiscountSelected);
    this.removeFromProductPromotionList(discountObj);
    if (CONSTANTS.SIEMENS_MATERIAL_TYPES.has(discountObj.materialDiscountSelected) && discountObj.discount > 0) {
      this.triggerProdProCostCalc(discountObj.discount, true);
    }
    this.addMaterialBackToDropdown(discountObj.materialDiscountSelected);
  }

  addMaterialBackToDropdown(materialType: string): void {
    const materialToAdd = this.originalAssignedMaterialsList.find(mat => mat.materialType === materialType);
    if (materialToAdd) {
      this.assignedMaterialsList.push(materialToAdd);
    }
  }

  removeFromProductPromotionList(discountObj: IProductPromotionAppliedDiscount): void {
    if (this.isReadOnly) {
      return;
    }

    const estimateSettings: EstimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    if (!estimateSettings || !estimateSettings.productPromotionList) {
      return;
    }
    const updatedPromotionList = estimateSettings.productPromotionList.filter(promotion => promotion.affectedMaterialText !== discountObj.materialDiscountSelected);
    estimateSettings.productPromotionList = updatedPromotionList;
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    estimateMetaData.estimateSettings = estimateSettings;
    this.commonDataService.patchEstimateMetaData(estimateMetaData);
    this.commonDataService.isSaveEnabled = true;
  }

  showMaterialDropdown(): boolean {
    if (this.business === CONSTANTS.BUSINESS_AUTOMATION
      || this.business === CONSTANTS.BUSINESS_ELECTRICAL
      || this.business === CONSTANTS.BUSINESS_MECHANICAL) {
      return false;
    }
    return true;
  }

  getAffectedMaterialValue(): string {
    const materialValue = this.productPromotionForm.controls.assignMaterialDiscountDropdown.value;
    if (materialValue && materialValue.length > 1) {
      const affectedMaterial = this.assignedMaterialsList.find(material => material.materialType === materialValue);
      if (affectedMaterial) {
        return affectedMaterial.materialId;
      }
      return materialValue
    }
    return materialValue || CONSTANTS.EMPTY_STRING;
  }

  enableApplyBtn(): boolean {
    if (this.isReadOnly) {
      return false;
    }

    const authCode = this.productPromotionForm.controls.authorizationCode.value;
    const selectedMaterialValue = this.getAssignedMaterialValue();
    const isMaterialAlreadyApplied = this.appliedDiscountsList.some(
      appliedDiscount => appliedDiscount.materialDiscountSelected === selectedMaterialValue
    );
    const isAuthCodeValid = !!authCode && authCode.length >= CONSTANTS.AUTHORIZATION_CODE_MIN_LENGTH;

    return !!selectedMaterialValue && isAuthCodeValid && !isMaterialAlreadyApplied && !!(this.assignedMaterialsList && this.assignedMaterialsList.length);
  }

  onAuthorizationCodeInput(event: Event): void {
    if (this.isReadOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const authorizationCode = input.value.replace(/[^A-Za-z0-9]/g, '');
    input.value = authorizationCode;
    this.productPromotionForm.controls.authorizationCode.setValue(authorizationCode);
  }

  getAffectedMaterialId(materialType: string): string {
    const material = this.assignedMaterialsList.find(mat => mat.materialType === materialType);
    if (material) {
      return material.materialId;
    }
    return CONSTANTS.EMPTY_STRING;
  }

  saveProductPromotions(promotion: IProductPromotionAppliedDiscount): void {
    if (this.isReadOnly) {
      return;
    }

    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    if (!estimateMetaData || !estimateMetaData.estimateSettings) {
      return;
    }
    if (!estimateMetaData.estimateSettings.productPromotionList) {
      estimateMetaData.estimateSettings.productPromotionList = new ProductPromotionCollection();
    }

    const model = new ProductPromotionModel();
    model.authorizationCode = promotion.authCode;
    model.affectedMaterialText = promotion.materialDiscountSelected;
    model.affectedMaterial = this.getAffectedMaterialId(promotion.materialDiscountSelected);
    model.discount = promotion.discount;
    model.allowableDiscountDollars = promotion.allowableDiscountDollars;
    model.allowableDiscountPercentage = promotion.allowableDiscountPercentage;
    model.isRequestPending = false;

    estimateMetaData.estimateSettings.productPromotionList.push(model);
    this.commonDataService.patchEstimateMetaData(estimateMetaData);
    this.commonDataService.isSaveEnabled = true;
  }
  onClose() {
    this.closeDialogEvent.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isEstimateSapSubmitted = false;
  }

  // Matched implementation with desktop; if no auth code is present, set the first auth code from the applied discounts list as default
  private setDefaults(): void {
    const authCodeControl = this.productPromotionForm.controls.authorizationCode;
    if (!authCodeControl.value && this.appliedDiscountsList.length > 0) {
      this.productPromotionForm.controls.authorizationCode.setValue(this.appliedDiscountsList[0]?.authCode);
      this.productPromotionForm.controls.allowableDiscountDollars.setValue(this.appliedDiscountsList[0]?.allowableDiscountDollars);
      this.productPromotionForm.controls.allowableDiscountPercentage.setValue(this.appliedDiscountsList[0]?.allowableDiscountPercentage);

    }
  }

  subscribeToAssignedDiscountPercent() {
    this.productPromotionForm.controls.allowableDiscountPercentage.valueChanges
      .pipe(startWith(this.productPromotionForm.controls.allowableDiscountPercentage.value), distinctUntilChanged(), debounceTime(300), takeUntil(this.destroy$))
      .subscribe(allowable => {
        this.numericFieldsConfig.assignMaterialDiscountConfig = {
          ...this.numericFieldsConfig.assignMaterialDiscountConfig,
          max: Math.min(allowable ?? 0, CONSTANTS.NINETY_NINE_POINT_NINE_NINE)
        };
      });
  }
}